import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, authenticatedFetch } from './utils/apiService';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  CircularProgress,
  SearchIcon,
  Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Search as SearchIconImport,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import FiltrosPeriodo from './FiltrosPeriodo';
import GraficoFacturadoProveedor from './GraficoFacturadoProveedor';

// Hook personalizado para debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  console.log('🔄 useDebounce - retornando:', debouncedValue);
  return debouncedValue;
};

// Chip de estado con colores
const StatusChip = styled(Chip)(({ theme, scraped }) => ({
  fontWeight: 'bold',
  color: 'white',
  backgroundColor: scraped ? '#4caf50' : '#ff9800',
  '& .MuiChip-icon': {
    color: 'white',
  },
}));

// Función para formatear el período de forma flexible
const formatearPeriodo = (mes) => {
  const nombresMeses = {
    Ene: 'Enero', Feb: 'Febrero', Mar: 'Marzo', Abr: 'Abril',
    May: 'Mayo', Jun: 'Junio', Jul: 'Julio', Ago: 'Agosto',
    Sep: 'Septiembre', Oct: 'Octubre', Nov: 'Noviembre', Dic: 'Diciembre'
  };

  const match = mes.match(/^([A-Za-z]+)(\d{2})$/);
  if (match) {
    const [, mesNombre, añoCorto] = match;
    // Convertir 2 dígitos a 4 dígitos usando regla Excel: 00-49 → 2000-2049, 50-99 → 1950-1999
    const añoNum = parseInt(añoCorto, 10);
    const añoCompleto = añoNum <= 49 ? 2000 + añoNum : 1900 + añoNum;
    return `${nombresMeses[mesNombre] || mesNombre} ${añoCompleto}`;
  }
  return mes; // si no coincide con formato esperado
};

// Función para agrupar movimientos por año usando el período formateado
const agruparMovimientosPorAño = (movimientos) => {
  const grupos = {};
  movimientos.forEach(mov => {
    let año;
    if (mov.año) {
      año = mov.año;
    } else if (mov.periodo) {
      const periodoFormateado = formatearPeriodo(mov.periodo);
      // Extraer el año del string formateado: "Enero 2024" → "2024"
      const match = periodoFormateado.match(/\d{4}$/);
      año = match ? match[0] : 'Desconocido';
    } else {
      año = 'Desconocido';
    }

    if (!grupos[año]) grupos[año] = [];
    grupos[año].push(mov);
  });
  return grupos;
};

const BuscadorProveedores = ({ 
  apiBase, 
  busquedaInicial = '', 
  provinciaFilter = '', 
  actividadFilter = '',
  onProveedorSelect = null
}) => {
  const [proveedores, setProveedores] = useState([]);
  const [busqueda, setBusqueda] = useState(busquedaInicial);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [filtrosPeriodo, setFiltrosPeriodo] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalProveedores, setTotalProveedores] = useState(0);
  const [error, setError] = useState(null);
  
  // Usar debounce para optimizar búsquedas
  const debouncedBusqueda = useDebounce(busqueda, 300);

  // Función optimizada de carga
  const cargarProveedores = useCallback(async (q = '', page = 1, reset = false) => {
    console.log('🔄 Iniciando carga de proveedores:', { q, page, reset });
    
    if (reset) {
      setLoading(true);
      setProveedores([]);
    } else {
      setLoadingMore(true);
    }
    
    try {
      // Usar límite consistente para evitar problemas de paginación
      const limit = 100;
      const offset = (page - 1) * limit;
      
      // Construir la URL con todos los filtros
      const params = new URLSearchParams();
      if (q && q.trim()) params.append('q', q.trim()); // Corregido: usar 'q' en lugar de 'busqueda'
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      
      if (filtrosPeriodo.mes) params.append('mes', filtrosPeriodo.mes);
      if (filtrosPeriodo.periodo) params.append('periodo', filtrosPeriodo.periodo);
      if (filtrosPeriodo.ano) params.append('ano', filtrosPeriodo.ano);
      
      // Mapear 'orden' a 'ordenar' para el backend
      const ordenamiento = filtrosPeriodo.orden || 'monto';
      params.append('ordenar', ordenamiento);
      
      const url = `${apiBase}/proveedores?${params.toString()}`;
      console.log('🌐 URL generada:', url);
      
      const response = await authenticatedFetch(url);
      const data = await response.json();
      
      console.log('✅ Respuesta del servidor:', {
        status: response.status,
        dataCount: data?.data?.length || 0,
        pagination: data?.pagination
      });
      
      if (data && Array.isArray(data.data)) {
        if (reset) {
          console.log('🔄 Estableciendo nuevos proveedores:', data.data.length);
          setProveedores(data.data);
          setCurrentPage(1);
        } else {
          setProveedores(prev => {
            console.log('➕ Agregando proveedores:', prev.length, '+', data.data.length);
            return [...prev, ...data.data];
          });
          setCurrentPage(page);
        }
        
        setHasMore(data.pagination?.hasMore && data.data.length > 0);
        setTotalProveedores(data.pagination?.total || 0);
      } else {
        console.error('❌ Formato de respuesta inválido:', data);
        setError('Formato de respuesta inválido del servidor');
      }
    } catch (error) {
      console.error('💥 Error al cargar proveedores:', error);
      setError(`Error al cargar proveedores: ${error.message}`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filtrosPeriodo]); // Dependencias del useCallback

  // Cargar cotizaciones de dólar al montar el componente
  useEffect(() => {
    const loadDollarRates = async () => {
      try {
        const { getDollarRates } = await import('./utils/dollarService');
        const rates = await getDollarRates();
        setDollarRates(rates);
        console.log('💵 Cotizaciones cargadas:', rates);
      } catch (error) {
        console.warn('⚠️ No se pudieron cargar las cotizaciones de dólar:', error);
      }
    };
    
    loadDollarRates();
  }, []);

  // Effect para cargar datos iniciales y manejar cambios de filtros y búsqueda
  useEffect(() => {
    console.log('🔄 Cargando datos iniciales - filtros:', filtrosPeriodo, 'búsqueda:', debouncedBusqueda);
    setProveedores([]);
    setCurrentPage(1);
    setHasMore(true);
    cargarProveedores(debouncedBusqueda, 1, true);
  }, [filtrosPeriodo, debouncedBusqueda, cargarProveedores]);

  // Hook para detectar scroll infinito optimizado con Intersection Observer
  useEffect(() => {
    const target = document.getElementById('scroll-trigger');
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) {
          cargarProveedores(debouncedBusqueda, currentPage + 1, false);
        }
      },
      { threshold: 1.0 }
    );

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [currentPage, loadingMore, hasMore, debouncedBusqueda, cargarProveedores]);

  // Función simplificada para búsqueda
  const handleBusquedaChange = useCallback((valor) => {
    console.log('🔍 Cambio en búsqueda:', valor);
    setBusqueda(valor);
  }, []);

  const verProveedor = async (proveedor) => {
    console.log('🔍 verProveedor llamada con:', proveedor);
    setSelectedProveedor(proveedor);
    setLoading(true);
    
    // Notificar al componente padre que se seleccionó un proveedor
    if (onProveedorSelect) {
      onProveedorSelect(proveedor);
    }
    
    try {
      // Obtener información completa del proveedor incluyendo ids_anteriores
      console.log('📡 Cargando datos del proveedor:', proveedor.id);
      const proveedorResponse = await authenticatedFetch(`${apiBase}/proveedores/${proveedor.id}`);
      
      // Verificar respuesta antes de parsear
      if (!proveedorResponse.ok) {
        const errorText = await proveedorResponse.text();
        console.error('❌ Error en respuesta del proveedor:', proveedorResponse.status, errorText);
        throw new Error(`Error ${proveedorResponse.status}: ${errorText}`);
      }
      
      const proveedorData = await proveedorResponse.json();
      console.log('👤 Datos del proveedor cargados:', proveedorData);
      setSelectedProveedor(proveedorData);
      
      // Obtener movimientos con filtros
      const params = new URLSearchParams();
      if (filtrosPeriodo.periodo) params.append('periodo', filtrosPeriodo.periodo);
      if (filtrosPeriodo.ano) params.append('ano', filtrosPeriodo.ano);
      if (filtrosPeriodo.orden) params.append('orden', filtrosPeriodo.orden);
      
      const url = `${apiBase}/proveedores/${proveedor.id}/movimientos${params.toString() ? '?' + params.toString() : ''}`;
      console.log('📊 Cargando movimientos desde:', url);
      const response = await authenticatedFetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error en respuesta de movimientos:', response.status, errorText);
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      
      const movimientosData = await response.json();
      console.log('💰 Movimientos cargados:', movimientosData.length, 'elementos');
      console.log('📈 Primeros 3 movimientos:', movimientosData.slice(0, 3));
      setMovimientos(movimientosData);
    } catch (error) {
      console.error('❌ Error completo cargando proveedor:', {
        message: error.message,
        stack: error.stack,
        proveedor: proveedor
      });
      alert(`Error cargando datos del proveedor: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = useCallback((nuevosFiltros) => {
    setFiltrosPeriodo(nuevosFiltros);
    setBusqueda('');
  }, []);

  const renderProveedorDetail = () => {
    const gruposMovimientos = agruparMovimientosPorAño(movimientos);
    
    return (
      <Box>
        <Button 
          variant="contained"
          size="large"
          startIcon={<ArrowBackIcon />}
          onClick={() => setSelectedProveedor(null)}
          sx={{ 
            mb: 4,
            backgroundColor: '#1976d2',
            color: 'white',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              backgroundColor: '#1565c0',
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          ← Volver a la Lista de Proveedores
        </Button>
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              {selectedProveedor.razon_social}
            </Typography>
            
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">
                    <strong>CUIT:</strong> {selectedProveedor.cuit}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Cuenta:</strong> {selectedProveedor.cuenta}
                  </Typography>
                  {selectedProveedor.ids_anteriores && (
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      <strong>Otras cuentas usadas:</strong> {selectedProveedor.ids_anteriores}
                    </Typography>
                  )}
                  {selectedProveedor.actividad && (
                    <Typography variant="body1">
                      <strong>Actividad:</strong> {selectedProveedor.actividad}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {selectedProveedor.provincia && (
                    <Typography variant="body1">
                      <strong>Provincia:</strong> {selectedProveedor.provincia}
                    </Typography>
                  )}
                  <StatusChip
                    scraped={selectedProveedor.scrapeado === 1}
                    icon={selectedProveedor.scrapeado === 1 ? <CheckCircleIcon /> : <ScheduleIcon />}
                    label={selectedProveedor.scrapeado === 1 ? "Datos completos" : "Info básica"}
                  />
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>

        {/* Movimientos por año */}
        {Object.keys(gruposMovimientos).sort().reverse().map(año => (
          <Accordion key={año} defaultExpanded={año === '2025'}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Año {año} ({gruposMovimientos[año].length} movimientos)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Vista móvil */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {gruposMovimientos[año].map((mov, index) => (
                  <Card key={index} sx={{ mb: 2, p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          {mov.periodo}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Facturado
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          ${(parseFloat(mov.facturado) || 0).toLocaleString('es-AR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Abonado
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          ${(parseFloat(mov.abonado) || 0).toLocaleString('es-AR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Saldo
                        </Typography>
                        <Typography 
                          variant="h6" 
                          fontWeight="bold"
                          sx={{ 
                            color: mov.saldo > 0 ? 'error.main' : 'success.main',
                          }}
                        >
                          ${(parseFloat(mov.saldo) || 0).toLocaleString('es-AR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </Box>

              {/* Vista desktop */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Período</TableCell>
                        <TableCell align="right">Facturado</TableCell>
                        <TableCell align="right">Abonado</TableCell>
                        <TableCell align="right">Saldo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {gruposMovimientos[año].map((mov, index) => (
                        <TableRow key={index}>
                          <TableCell>{mov.periodo}</TableCell>
                          <TableCell align="right">
                            ${(parseFloat(mov.facturado) || 0).toLocaleString('es-AR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                          <TableCell align="right">
                            ${(parseFloat(mov.abonado) || 0).toLocaleString('es-AR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ 
                              color: mov.saldo > 0 ? 'error.main' : 'success.main',
                              fontWeight: 'bold'
                            }}
                          >
                            ${(parseFloat(mov.saldo) || 0).toLocaleString('es-AR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Gráficos de facturado por período */}
        {movimientos.length > 0 && (
          <>
            {console.log('📊 Renderizando GraficoFacturadoProveedor con', movimientos.length, 'movimientos para', selectedProveedor.razon_social)}
            <GraficoFacturadoProveedor 
              movimientos={movimientos}
              proveedorNombre={selectedProveedor.razon_social}
            />
          </>
        )}
        {movimientos.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {console.log('⚠️ No hay movimientos para mostrar gráficos')}
            No hay movimientos para mostrar gráficos
          </Typography>
        )}
      </Box>
    );
  };

  if (selectedProveedor) {
    return renderProveedorDetail();
  }

  return (
    <Box>
      {/* Layout principal: Búsqueda arriba, filtros abajo */}
      <Box sx={{ mb: 3 }}>
        {/* Cuadro de búsqueda que ocupa el ancho completo */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            borderRadius: '20px',
            padding: '6px',
            boxShadow: '0 8px 32px rgba(25, 118, 210, 0.2)'
          }}>
            <TextField
              fullWidth
              placeholder="🔍 Buscar proveedor por nombre, CUIT, provincia o actividad..."
              value={busqueda}
              onChange={(e) => handleBusquedaChange(e.target.value)}
              InputProps={{
                startAdornment: <SearchIconImport sx={{ mr: 2, color: '#1976d2', fontSize: '24px' }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '18px',
                  padding: '18px 24px',
                  borderRadius: '16px',
                  backgroundColor: 'white',
                  border: 'none',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover': {
                    backgroundColor: '#fafafa',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.2)'
                  }
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '18px',
                  fontWeight: '500',
                  '&::placeholder': {
                    color: '#6c757d',
                    opacity: 1
                  }
                }
              }}
            />
          </Box>
        </Box>
        
        {/* Filtros por período debajo de la búsqueda */}
        <Box>
          <FiltrosPeriodo 
            onFiltroChange={handleFiltroChange}
            filtrosActivos={filtrosPeriodo}
          />
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 3, 
            maxWidth: '1200px',
            margin: '0 auto',
            padding: 2,
            '@media (max-width: 1024px)': {
              gridTemplateColumns: 'repeat(2, 1fr)'
            },
            '@media (max-width: 768px)': {
              gridTemplateColumns: '1fr'
            }
          }}
        >
          {proveedores.map(p => (
            <Card 
              key={p.id}
              onClick={() => verProveedor(p)}
              sx={{
                height: 280,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                  '& .MuiCardContent-root': {
                    backgroundColor: '#f8f9fa'
                  }
                },
                boxShadow: 2
              }}
            >
              <CardContent sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                padding: 3,
                minHeight: '420px'
              }}>
                <Typography 
                  variant="h6" 
                  component="h3"
                  gutterBottom
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 600,
                    minHeight: '6rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.4,
                    mb: 2
                  }}
                >
                  {p.razon_social}
                </Typography>
                
                <Box sx={{ minHeight: '140px', mb: 2, overflow: 'hidden' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>CUIT:</strong> {p.cuit}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Cuenta:</strong> {p.cuenta}
                  </Typography>
                  
                  {p.provincia && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Provincia:</strong> {p.provincia}
                    </Typography>
                  )}
                  
                  {p.actividad && (
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      <strong>Actividad:</strong> {p.actividad.length > 50 ? 
                        p.actividad.substring(0, 50) + '...' : 
                        p.actividad
                      }
                    </Typography>
                  )}
                </Box>
                
                {((p.promedio_facturacion_año && p.promedio_facturacion_año > 0) || (p['promedio_facturacion_año'] && p['promedio_facturacion_año'] > 0)) && (
                  <Box sx={{ 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: 2, 
                    padding: 1.5,
                    border: '1px solid #e0e0e0',
                    mt: 'auto'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                      📊 Facturación promedio {p.año_promedio || p['año_promedio'] || '2025'}:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {new Intl.NumberFormat('es-AR', {
                        style: 'currency',
                        currency: 'ARS',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(p.promedio_facturacion_año || p['promedio_facturacion_año'] || 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                      Basado en {p.meses_con_datos || 0} {p.meses_con_datos === 1 ? 'mes' : 'meses'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
      
      {/* Trigger invisible para Intersection Observer */}
      {hasMore && !loading && (
        <div id="scroll-trigger" style={{ height: '10px', visibility: 'hidden' }}></div>
      )}
      
      {/* Indicador de carga al final */}
      {loadingMore && (
        <Box display="flex" justifyContent="center" p={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Cargando más proveedores...
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Indicador cuando se han cargado todos */}
      {!loading && !loadingMore && !hasMore && proveedores.length > 0 && (
        <Box display="flex" justifyContent="center" p={3}>
          <Typography variant="body2" color="text.secondary">
            ✅ Se han cargado todos los {totalProveedores} proveedores
          </Typography>
        </Box>
      )}
      
      {/* Mensaje cuando no hay resultados */}
      {!loading && !loadingMore && proveedores.length === 0 && (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron proveedores que coincidan con la búsqueda
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BuscadorProveedores;